import { Box, Button, MenuItem, Typography } from '@mui/material'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { StyledMenu } from 'components/StyledMenu/StyledMenu'
import ListBullet from 'assets/format_list_bulleted.svg'
import { useState } from 'react'
import './styles.css'
import { Modal } from 'components/Modal/Modal'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useLayout } from 'hooks/useLayout'
import { colorPrimitives } from 'components/Theme'
import { usePutSweepstakeWinner } from 'hooks/api/Sweepstake/usePutSweepstakeWinner'
import { usePutSweepstakeIfAllWinnersAwarded } from 'hooks/api/Sweepstake/usePutSweepstakeIfAllWinnersAwarded'
import { getSweepstakeDrawingInfoQueryKey } from 'hooks/api/useGetSweepstakeByIdWithDrawingInfo'
import { useQueryClient } from '@tanstack/react-query'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { type LocationWinnerTableProps } from './LocationWinnersColumns'
import { SweepstakeNotifyWinnersModal } from 'components/Sweepstakes/SweepstakeNotifyWinnersModal'
// we are using the location sweepstakes validate winners modal for location winners here...per Rachael, this is ok
import { LocationSweepstakesValidateWinnerModal } from 'pages/Contests/LocationSweepstakes/LocationSweepstakesWinnersPage/LocationSweepstakesValidateWinnerModal'

interface RedrawWinnerMenuItemProps {
  handleClose: () => void
  setIsIndividualRedrawModalOpen: () => void
}

export const RedrawWinnerMenuItem = ({
  handleClose,
  setIsIndividualRedrawModalOpen,
}: RedrawWinnerMenuItemProps) => (
  <MenuItem
    onClick={() => {
      handleClose()
      setIsIndividualRedrawModalOpen()
    }}
  >
    <Box className="py-2">
      <Typography variant="body-2">Redraw Winner</Typography>
    </Box>
  </MenuItem>
)

export const LocationPrizeMenu = ({
  params,
  setIsIndividualRedrawModalOpen,
  sweepstakeName,
  isLocationSweepstake,
  contestId,
}: {
  params: GridRenderCellParams<LocationWinnerTableProps>
  setIsIndividualRedrawModalOpen: () => void
  sweepstakeName: string
  isLocationSweepstake: boolean
  contestId: number
}) => {
  const [notifyWinnersModalOpen, setNotifyWinnersModalOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const qc = useQueryClient()
  const { isMobile } = useLayout()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModalIsOpen = () => {
    setIsModalOpen(!isModalOpen)
  }
  const heightModal = isMobile ? '100%' : 'auto'
  const [selectedSweepstakeWinnerId, setSelectedSweepstakeWinnerId] = useState<
    number | undefined
  >()
  const [
    isSweepstakesValidateWinnerModalOpen,
    setIsSweepstakesValidateWinnerModalOpen,
  ] = useState(false)
  const toggleSweepstakesValidateWinnerModalOpen = () =>
    setIsSweepstakesValidateWinnerModalOpen(
      !isSweepstakesValidateWinnerModalOpen
    )

  const toggleMenu = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation()
    if (anchorEl === null) {
      setSelectedSweepstakeWinnerId(id)
      setAnchorEl(event.currentTarget)
    } else {
      setAnchorEl(null)
      setSelectedSweepstakeWinnerId(undefined)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = anchorEl !== null

  const status = params.row.winnerStatus

  const { mutateAsync: mutateSweepstakeAsync } =
    usePutSweepstakeIfAllWinnersAwarded({
      sweepstakeId: contestId,
      onError: () => {},
      onSuccess: () => {},
    })

  const { mutateAsync } = usePutSweepstakeWinner({
    onError: () => {},
    onSuccess: async (data) => {
      await qc.invalidateQueries({
        queryKey: getSweepstakeDrawingInfoQueryKey(contestId),
        exact: true,
      })
      setAnchorEl(null)
      // If winner has been awarded, check if all winners are awarded and mark contest as "Closed"
      if (data.status === 'Awarded') {
        await mutateSweepstakeAsync()
      }
    },
  })

  const postMessage = useTypedApiClient({
    path: '/admin/patrons/messages',
    method: 'post',
  })

  if (status === 'NoWinner') {
    return null
  }

  const sendPrizeConfirmation = async () => {
    await mutateAsync({
      req: {
        id: selectedSweepstakeWinnerId,
        status: 'WinnerNotified',
        sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
        contestEntryId: params.row.dto.contestEntryId,
        patronId: params.row.dto.patronId,
        dateDrawn: params.row.drawnDate,
        notes: params.row.dto.notes,
        drawingSequence: params.row.drawingSequence ?? 0,
        affidavitCompletedAt: params.row.dto.affidavitCompletedAt,
      },
    })
    await postMessage({
      patronMessageTemplateType: 'Custom',
      patronId: params.row.dto.patronId,
      sender: params.row.leName,
      subject: 'Confirmation of Prize Details',
      body: `We've received all the required details. You can expect to receive your prize within 4-6 weeks.\n\nContinue checking in on the J&J Gaming app to earn points for prizes!`,
      patronMessageTemplateId: 0,
      patronMessageData: {
        patronMessageType: 'PrizeConfirmation',
        sweepstakeWinnerId: params.row.id,
      },
    })
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation()
          toggleMenu(e, params.row.id)
        }}
        style={{
          minWidth: '50px',
        }}
      >
        <img src={ListBullet} width={4} height={16} alt="options icon" />
      </Button>
      <StyledMenu
        sx={{ mt: 0 }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {[
          status === 'LocationValidation' && (
            <MenuItem
              key="validateWinner"
              onClick={() => setIsSweepstakesValidateWinnerModalOpen(true)}
            >
              <Box className="py-2">
                <Typography variant="body-2">Validate Winner</Typography>
              </Box>
            </MenuItem>
          ),
          status === 'WinnerValidation' && (
            <MenuItem
              key="notifyWinner"
              onClick={() => {
                handleClose()
                setNotifyWinnersModalOpen(true)
              }}
            >
              <Box className="py-2">
                <Typography variant="body-2">Notify Winner</Typography>
              </Box>
            </MenuItem>
          ),
          status === 'WinnerNotified' && (
            <>
              <MenuItem
                key="affidavitReceived"
                onClick={async () =>
                  await mutateAsync({
                    req: {
                      id: selectedSweepstakeWinnerId,
                      status: 'AffidavitReceived',
                      sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
                      contestEntryId: params.row.dto.contestEntryId,
                      patronId: params.row.patronId,
                      dateDrawn: params.row.drawnDate,
                      notes: params.row.dto.notes,
                      drawingSequence: params.row.drawingSequence ?? 0,
                      contestWinnerId: params.row.contestWinnerId,
                      affidavitCompletedAt: null, // Set as null because the backend code will set it to current date time if null
                    },
                  })
                }
              >
                <Box className="py-2">
                  <Typography variant="body-2">Affidavit Received</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                key="notifyWinner"
                onClick={() => {
                  handleClose()
                  setNotifyWinnersModalOpen(true)
                }}
              >
                <Box className="py-2">
                  <Typography variant="body-2">Notify Winner</Typography>
                </Box>
              </MenuItem>
            </>
          ),
          status === 'AffidavitReceived' && (
            <MenuItem
              key="sentToAP"
              onClick={async () =>
                await mutateAsync({
                  req: {
                    id: selectedSweepstakeWinnerId,
                    status: 'SentToAP',
                    sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
                    contestEntryId: params.row.dto.contestEntryId,
                    patronId: params.row.patronId,
                    dateDrawn: params.row.drawnDate,
                    notes: params.row.dto.notes,
                    drawingSequence: params.row.drawingSequence ?? 0,
                    contestWinnerId: params.row.contestWinnerId,
                    affidavitCompletedAt: params.row.dto.affidavitCompletedAt,
                  },
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Sent To AP</Typography>
              </Box>
            </MenuItem>
          ),
          status === 'SentToAP' && (
            <MenuItem
              key="awarded"
              onClick={async () =>
                await mutateAsync({
                  req: {
                    id: selectedSweepstakeWinnerId,
                    status: 'Awarded',
                    sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
                    contestEntryId: params.row.dto.contestEntryId,
                    patronId: params.row.patronId,
                    dateDrawn: params.row.drawnDate,
                    notes: params.row.dto.notes,
                    drawingSequence: params.row.drawingSequence ?? 0,
                    contestWinnerId: params.row.contestWinnerId,
                    affidavitCompletedAt: params.row.dto.affidavitCompletedAt,
                  },
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Award Winner</Typography>
              </Box>
            </MenuItem>
          ),

          <RedrawWinnerMenuItem
            key="redrawWinner"
            handleClose={handleClose}
            setIsIndividualRedrawModalOpen={setIsIndividualRedrawModalOpen}
          />,
        ].filter(Boolean)}
      </StyledMenu>
      <Modal
        isOpen={isModalOpen}
        toggleIsOpen={toggleModalIsOpen}
        sx={{
          width: isMobile ? '100%' : '60%',
          height: 'auto',
        }}
      >
        <ModalFullHeader
          toggleIsOpen={toggleModalIsOpen}
          title={'Prize Confirmation Details'}
          subTitle={`${params.row.leName} (${params.row.leLicenseNumber})`}
        />
        <ModalFullBody heightModal={heightModal}>
          <Typography
            component="h3"
            variant="body-1"
            fontSize={'18px'}
            fontWeight={'bold'}
            paddingBottom={1.5}
          >
            Are you sure you want to send prize confirmation details to this
            winner?
          </Typography>
          <Typography variant="body-1" color={colorPrimitives.gray70}>
            {`Selecting "Send Confirmation" will send a confirmation message
            acknowledging the receipt of their affidavit and providing details
            regarding the expected delivery of their prize.`}
          </Typography>
          <Typography variant="body-1" paddingTop={5} paddingBottom={2}>
            <strong>{`${params.row.leName}`}</strong>{' '}
            {`(${params.row.leLicenseNumber}): ${params.row.winnerName}`}
          </Typography>
        </ModalFullBody>
        <ModalFullFooter>
          <Button onClick={toggleModalIsOpen}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            onClick={sendPrizeConfirmation}
          >
            Send Confirmation
          </Button>
        </ModalFullFooter>
      </Modal>
      <LocationSweepstakesValidateWinnerModal
        winnerName={params.row.winnerName}
        isModalOpen={isSweepstakesValidateWinnerModalOpen}
        toggleIsOpen={toggleSweepstakesValidateWinnerModalOpen}
        onConfirm={async () => {
          toggleSweepstakesValidateWinnerModalOpen()
          await mutateAsync({
            req: {
              id: selectedSweepstakeWinnerId,
              status: 'WinnerValidation',
              sweepstakeDrawingId: params.row.dto.sweepstakeDrawingId,
              contestEntryId: params.row.dto.contestEntryId,
              patronId: params.row.patronId,
              dateDrawn: params.row.drawnDate,
              notes: params.row.dto.notes,
              contestWinnerId: params.row.contestWinnerId,
              drawingSequence: params.row.drawingSequence ?? 0,
            },
          })
          handleClose()
        }}
        onCancel={() => {
          toggleSweepstakesValidateWinnerModalOpen()
          handleClose()
        }}
      />
      <SweepstakeNotifyWinnersModal
        sweepstakeName={sweepstakeName}
        isModalOpen={notifyWinnersModalOpen}
        toggleIsOpen={() => setNotifyWinnersModalOpen(!notifyWinnersModalOpen)}
        selectedWinners={[
          {
            locationName: params.row.leName,
            licenseNumber: params.row.leLicenseNumber,
            winnerName: params.row.winnerName,
            sweepstakesWinner: params.row.dto,
            drawingSequence: params.row.drawingSequence ?? 0,
          },
        ]}
        isLocationSweepstake={isLocationSweepstake}
        invalidateQueryKeys={[getSweepstakeDrawingInfoQueryKey(contestId)]}
      />
    </>
  )
}
